import { useState, useEffect } from "react"
import { Tab, Tabs, FloatingLabel, Form, Button } from "react-bootstrap"

import { searchPlaces, getPlaceDetails } from "../services/placesConnector"
import { getEntitiesData } from "../services/chaperonConnector"
import { fetchCommunityPlaces } from "../utilities/communities"
import { consoleLog } from "../utilities/helpers"
import { FLAGS_API_URL } from "../const/paths"
import { SCORE_THRESHOLD_MEDIUM } from "../const/numbers"
import googleLogo from "../images/google_on_white_hdpi.png"
import "./SearchBox.css"

export function CommunityMemberAdd({ onClose, onSubmit, addToast }) {
    const [activeTab, setActiveTab] = useState("person")
    const [communities, setCommunities] = useState([])
    const [selectedCommunity, setSelectedCommunity] = useState("")
    const [createNewCommunity, setCreateNewCommunity] = useState(false)
    const [newCommunityName, setNewCommunityName] = useState("")

    // Person tab state
    const [userIdentifier, setUserIdentifier] = useState("")
    const [personRole, setPersonRole] = useState("citizen")

    // Place tab state
    const [placeSearchInput, setPlaceSearchInput] = useState("")
    const [placeSuggestions, setPlaceSuggestions] = useState([])
    const [selectedPlace, setSelectedPlace] = useState(null)
    const [isLoadingPlace, setIsLoadingPlace] = useState(false)

    // Fetch user's communities on mount and when communities are updated
    useEffect(() => {
        const fetchUserCommunities = async () => {
            try {
                const storedUser = JSON.parse(
                    localStorage.getItem("chaperon_user_data"),
                )
                if (storedUser?.id) {
                    const userCommunities = await fetchCommunityPlaces(
                        storedUser.id,
                    )
                    setCommunities(userCommunities || [])
                    if (userCommunities && userCommunities.length > 0) {
                        setSelectedCommunity(userCommunities[0].id.toString())
                    }
                }
            } catch (error) {
                console.error("Error fetching communities:", error)
            }
        }

        fetchUserCommunities()

        // Listen for community updates from parent dashboard
        const handleCommunitiesUpdated = () => {
            consoleLog("Communities updated event received, refetching...")
            fetchUserCommunities()
        }

        window.addEventListener("communitiesUpdated", handleCommunitiesUpdated)

        return () => {
            window.removeEventListener(
                "communitiesUpdated",
                handleCommunitiesUpdated,
            )
        }
    }, [])

    // Handle place search suggestions
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (placeSearchInput.length > 2) {
                const results = await searchPlaces(placeSearchInput)
                setPlaceSuggestions(results)
            } else {
                setPlaceSuggestions([])
            }
        }

        const timeoutId = setTimeout(fetchSuggestions, 300)
        return () => clearTimeout(timeoutId)
    }, [placeSearchInput])

    const handleCommunityChange = (e) => {
        const value = e.target.value
        if (value === "new") {
            setCreateNewCommunity(true)
            setSelectedCommunity("")
        } else {
            setCreateNewCommunity(false)
            setSelectedCommunity(value)
        }
    }

    const handlePlaceSelection = async (placeId) => {
        setIsLoadingPlace(true)
        addToast("Finding your selected place...")

        try {
            // Get place details from Google
            const placeDetails = await getPlaceDetails(placeId)

            if (placeDetails) {
                setPlaceSearchInput("")
                setPlaceSuggestions([])
                consoleLog("Selected place:", placeDetails)

                // Get country from address components
                let countryComponent = placeDetails.address_components.find(
                    (component) => component.types.includes("country"),
                )
                let addressCountry = countryComponent
                    ? countryComponent.long_name
                    : null

                if (!addressCountry) {
                    console.error(
                        "Could not derive country from place details:",
                        placeDetails,
                    )
                    addToast(
                        `We couldn't find enough data for this location: ${placeDetails.name}`,
                    )
                    return
                }

                // Get entity data from Chaperon API
                let placeDataArray = await getEntitiesData(
                    placeDetails.name,
                    addressCountry,
                    placeDetails.formatted_address,
                )
                consoleLog("Place data array:", placeDataArray)

                if (placeDataArray && placeDataArray.length > 0) {
                    const placeData = placeDataArray[0]
                    placeData.fromGoogle = placeDetails
                    setSelectedPlace(placeData)
                    consoleLog("Place entity selected:", placeData)
                    addToast(`${placeDetails.name} selected`)
                } else {
                    addToast(`No entity found for ${placeDetails.name}`)
                }
            }
        } catch (error) {
            console.error("Error selecting place:", error)
            addToast("Failed to select place")
        } finally {
            setIsLoadingPlace(false)
        }
    }

    const handlePersonSubmit = async (e) => {
        e.preventDefault()

        if (!userIdentifier.trim()) {
            addToast("Please enter a username or email address")
            return
        }

        if (!selectedCommunity && !createNewCommunity) {
            addToast("Please select a community")
            return
        }

        // Check if user already exists in any community
        let userExists = false
        try {
            const communitiesData = localStorage.getItem(
                "chaperon_user_communities",
            )
            if (communitiesData) {
                const { communities: userCommunities } =
                    JSON.parse(communitiesData)
                if (userCommunities && Array.isArray(userCommunities)) {
                    // Check all person members across all communities
                    userExists = userCommunities.some((community) => {
                        if (community.chaperees) {
                            const chaperees =
                                typeof community.chaperees === "string"
                                    ? JSON.parse(community.chaperees)
                                    : community.chaperees
                            return chaperees.some(
                                (chaperee) =>
                                    chaperee.type === "person" &&
                                    (chaperee.username === userIdentifier ||
                                        chaperee.email === userIdentifier),
                            )
                        }
                        return false
                    })
                }
            }
        } catch (error) {
            console.error("Error checking if user exists:", error)
        }

        const memberData = {
            type: "person",
            identifier: userIdentifier,
            role: personRole,
            communityId: createNewCommunity ? null : selectedCommunity,
            newCommunityName: createNewCommunity ? newCommunityName : null,
            threshold: SCORE_THRESHOLD_MEDIUM,
            status: userExists ? "active" : "invited",
        }
        // TODO: Formulate CommunityData: { id, user_id, name, chaperees, chiefs }
        //  chaperees: array of person member identifiers: [{"id": 3, "type": "person", "username": "odsamuels", "role": "chief", "updated_at": "2025-11-18T10:05:42Z", "overall_safety": "19", "profile_picture_url": "https://www.odsamuels.com/static/media/odsamuels-professional.39de21e777cf8c04cb4f.jpeg"}, {"id": 186, "name": "Iraq", "type": "place", "updated_at": "2025-11-09T12:30:45Z"}, {"id": 4060, "name": "San Francisco", "type": "place", "updated_at": "2025-11-08T09:15:22Z"}, {"id": 1767, "name": "Dublin, Ireland", "type": "place", "updated_at": "2025-11-07T14:20:10Z"}, {"id": 4342, "name": "Benghazi, Libya", "type": "place", "updated_at": "2025-11-06T18:45:33Z"}, {"id": 1250, "name": "Paris", "type": "place", "updated_at": "2025-11-05T11:22:18Z"}, {"id": 1, "type": "person", "username": "sammy", "role": "citizen", "updated_at": "2025-11-09T10:05:42Z", "profile_picture_url": "https://storage.googleapis.com/chaperon/reports/1741925929135-Galina3.jpg"}]
        //  chiefs: array of person member identifiers: [{"id": 3, "role": "creator"}, {"id": 2, "role": "chief"}]
        // TODO: if communityId is not null, add CommunityData
        // TODO: else create new community and then add CommunityData

        consoleLog("Submitting person member:", memberData)

        try {
            if (onSubmit) {
                await onSubmit(memberData)
            }

            // TODO: If user exists and already in community, add person as community member directly
            // TODO: Else, send invitation to userIdentifier to join community
            // Invitation is created via API - Added to the invitations table; notification generated
            // Notification generates Alert
            // Invitation email is sent via backend

            //Reset form fields after successful submission
            setUserIdentifier("")
            setPersonRole("citizen")

            onClose()
        } catch (error) {
            console.error("Error adding person member:", error)
            addToast("Failed to send invitation")
        }
    }

    const handlePlaceSubmit = async (e) => {
        e.preventDefault()

        if (!selectedPlace) {
            addToast("Please search for and select a place")
            return
        }

        if (!selectedCommunity && !createNewCommunity) {
            addToast("Please select a community")
            return
        }

        const memberData = {
            type: "place",
            place: selectedPlace,
            communityId: createNewCommunity ? null : selectedCommunity,
            newCommunityName: createNewCommunity ? newCommunityName : null,
            threshold: SCORE_THRESHOLD_MEDIUM,
        }

        consoleLog("Submitting place member:", memberData)

        try {
            if (onSubmit) {
                await onSubmit(memberData)
            }

            onClose()
        } catch (error) {
            console.error("Error adding place member:", error)
            addToast("Failed to add place")
        }
    }

    return (
        <div className="modal-content">
            <div className="modal-header mb-3">
                <h4>
                    <i className="bi bi-person-plus-fill pe-2"></i>
                    Add Community Member
                </h4>
            </div>
            <div className="modal-body p-3">
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-3"
                >
                    <Tab
                        eventKey="person"
                        title={
                            <span>
                                <i className="bi bi-person m-1"></i> Person
                            </span>
                        }
                    >
                        <Form onSubmit={handlePersonSubmit}>
                            {/* Community Selection */}
                            <FloatingLabel
                                controlId="personCommunitySelect"
                                label="Select Community"
                                className="mb-3"
                            >
                                <Form.Select
                                    value={
                                        createNewCommunity
                                            ? "new"
                                            : selectedCommunity
                                    }
                                    onChange={handleCommunityChange}
                                >
                                    {communities.length === 0 && (
                                        <option value="">
                                            No communities found
                                        </option>
                                    )}
                                    {communities.map((community) => (
                                        <option
                                            key={community.id}
                                            value={community.id}
                                        >
                                            {community.name}
                                        </option>
                                    ))}
                                    <option value="new">
                                        + Create New Community
                                    </option>
                                </Form.Select>
                            </FloatingLabel>

                            {/* New Community Name Input */}
                            {createNewCommunity && (
                                <FloatingLabel
                                    controlId="newPersonCommunityName"
                                    label="New Community Name"
                                    className="mb-3"
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter community name"
                                        value={newCommunityName}
                                        onChange={(e) =>
                                            setNewCommunityName(e.target.value)
                                        }
                                        maxLength="100"
                                        required={createNewCommunity}
                                    />
                                </FloatingLabel>
                            )}

                            {/* User Identifier Input */}
                            <FloatingLabel
                                controlId="userIdentifier"
                                label="Username or Email Address"
                                className="mb-3"
                            >
                                <Form.Control
                                    type="text"
                                    placeholder="Enter username or email"
                                    value={userIdentifier}
                                    onChange={(e) =>
                                        setUserIdentifier(e.target.value)
                                    }
                                    maxLength="100"
                                    required
                                />
                            </FloatingLabel>

                            {/* Role Selection */}
                            <FloatingLabel
                                controlId="personRoleSelect"
                                label="Role in Community"
                                className="mb-3"
                            >
                                <Form.Select
                                    value={personRole}
                                    onChange={(e) =>
                                        setPersonRole(e.target.value)
                                    }
                                >
                                    <option value="citizen">Citizen</option>
                                    <option value="chief">Chief</option>
                                </Form.Select>
                            </FloatingLabel>

                            {/* Submit Buttons */}
                            <div className="d-flex gap-2 justify-content-end">
                                <Button variant="dark" type="submit">
                                    <i className="bi bi-send me-2"></i>
                                    Send Invite
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </Form>
                    </Tab>

                    <Tab
                        eventKey="place"
                        title={
                            <span>
                                <i className="bi bi-geo-alt m-1"></i> Place
                            </span>
                        }
                    >
                        <Form onSubmit={handlePlaceSubmit}>
                            {/* Community Selection */}
                            <FloatingLabel
                                controlId="placeCommunitySelect"
                                label="Select Community"
                                className="mb-3"
                            >
                                <Form.Select
                                    value={
                                        createNewCommunity
                                            ? "new"
                                            : selectedCommunity
                                    }
                                    onChange={handleCommunityChange}
                                >
                                    {communities.length === 0 && (
                                        <option value="">
                                            No communities found
                                        </option>
                                    )}
                                    {communities.map((community) => (
                                        <option
                                            key={community.id}
                                            value={community.id}
                                        >
                                            {community.name}
                                        </option>
                                    ))}
                                    <option value="new">
                                        + Create New Community
                                    </option>
                                </Form.Select>
                            </FloatingLabel>

                            {/* New Community Name Input */}
                            {createNewCommunity && (
                                <FloatingLabel
                                    controlId="newPlaceCommunityName"
                                    label="New Community Name"
                                    className="mb-3"
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter community name"
                                        value={newCommunityName}
                                        onChange={(e) =>
                                            setNewCommunityName(e.target.value)
                                        }
                                        maxLength="100"
                                        required={createNewCommunity}
                                    />
                                </FloatingLabel>
                            )}

                            {/* Place Search Box */}
                            <Form.Label>Search for Place</Form.Label>
                            <div
                                className="mb-3"
                                style={{ minHeight: "250px" }}
                            >
                                <div className="d-flex align-items-center">
                                    {selectedPlace &&
                                        selectedPlace?.country_code && (
                                            <img
                                                src={`${FLAGS_API_URL}/${selectedPlace?.country_code}.svg`}
                                                alt={
                                                    selectedPlace?.fromGoogle
                                                        ?.name ||
                                                    selectedPlace.name
                                                }
                                                title={
                                                    selectedPlace?.fromGoogle
                                                        ?.name ||
                                                    selectedPlace.name
                                                }
                                                style={{
                                                    width: "32px",
                                                    height: "auto",
                                                    marginRight: "8px",
                                                }}
                                            />
                                        )}
                                    <div className="dropdown flex-grow-1">
                                        <Form.Control
                                            id="placeSearchInputBox"
                                            value={placeSearchInput}
                                            onChange={(e) =>
                                                setPlaceSearchInput(
                                                    e.target.value,
                                                )
                                            }
                                            type="search"
                                            placeholder={
                                                selectedPlace
                                                    ? selectedPlace?.fromGoogle
                                                          ?.name ||
                                                      selectedPlace.name
                                                    : "Search for a place..."
                                            }
                                            autoComplete="off"
                                            disabled={isLoadingPlace}
                                        />
                                        {placeSuggestions.length > 0 && (
                                            <ul
                                                id="placeSearchSuggestions"
                                                className="dropdown-menu show"
                                                style={{
                                                    width: "100%",
                                                    maxHeight: "200px",
                                                    overflowY: "auto",
                                                }}
                                            >
                                                {placeSuggestions.map(
                                                    (suggestion) => (
                                                        <li
                                                            key={
                                                                suggestion.place_id
                                                            }
                                                        >
                                                            <button
                                                                className="dropdown-item"
                                                                type="button"
                                                                onClick={() =>
                                                                    handlePlaceSelection(
                                                                        suggestion.place_id,
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-geo-alt me-2"></i>
                                                                {
                                                                    suggestion.description
                                                                }
                                                            </button>
                                                        </li>
                                                    ),
                                                )}
                                                <span
                                                    className="pe-2"
                                                    style={{ float: "right" }}
                                                >
                                                    <img
                                                        src={googleLogo}
                                                        alt="Powered by Google"
                                                        title="Powered by Google"
                                                    />
                                                </span>
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Submit Buttons */}
                            <div className="d-flex gap-2 justify-content-end">
                                <Button variant="dark" type="submit">
                                    <i className="bi bi-geo-alt-fill me-2"></i>
                                    Add Place
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </Form>
                    </Tab>
                </Tabs>
            </div>
        </div>
    )
}
